import { Link } from 'react-router-dom';
import { ChartBarIcon, ClockIcon } from '@heroicons/react/24/outline';

interface NetWorthEmptyStateProps {
    /** Whether the user has any ledgers at all */
    hasAnyLedgers: boolean;
    /** Whether data is still loading */
    isLoading?: boolean;
    /** Number of data points available (for single data point case) */
    dataPointCount?: number;
}

/**
 * Empty state component for the Net Worth chart.
 * Shows different messages based on the state of user data.
 */
export function NetWorthEmptyState({
    hasAnyLedgers,
    isLoading = false,
    dataPointCount = 0,
}: NetWorthEmptyStateProps) {
    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="animate-pulse">
                    <ChartBarIcon className="h-16 w-16 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                    Loading your financial data...
                </p>
            </div>
        );
    }

    // No ledgers at all
    if (!hasAnyLedgers) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <ChartBarIcon className="h-16 w-16 text-gray-400 dark:text-gray-500" />
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                    Start Tracking Your Net Worth
                </h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                    Add your first asset or debt to see your financial progress over time.
                </p>
                <div className="mt-6 flex flex-col items-center gap-2">
                    <Link
                        to="/assets"
                        className="inline-flex items-center px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors"
                    >
                        Add Your First Asset
                    </Link>
                    <Link
                        to="/debts"
                        className="text-sm text-accent hover:text-accent/80 transition-colors"
                    >
                        or start with Debts →
                    </Link>
                </div>
            </div>
        );
    }

    // Has ledgers but only 1 data point
    if (dataPointCount <= 1) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <ClockIcon className="h-16 w-16 text-gray-400 dark:text-gray-500" />
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                    Not enough data yet
                </h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                    Record values monthly (end of month works best) to track trends.
                    Charts appear after 2+ months of data.
                </p>
                <Link
                    to="/assets"
                    className="mt-6 inline-flex items-center px-4 py-2 text-accent hover:text-accent/80 transition-colors"
                >
                    Go to Assets
                </Link>
            </div>
        );
    }

    // Default empty state (shouldn't normally reach here)
    return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
            <ChartBarIcon className="h-16 w-16 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                No data to display
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                Add some financial data to see your net worth chart.
            </p>
        </div>
    );
}
